import { useEffect, useRef, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { Swords, Loader2, X } from "lucide-react";

/**
 * BattleOverlay: shown on the streamer screen while live.
 * - Polls GET /battles/invites/pending and shows an Accept / Decline popup for incoming PK battle invites.
 * - Polls GET /battles/mine/current and shows a banner: waiting for opponent, or battle countdown.
 * - When the countdown hits zero the host closes the battle (POST /battles/{id}/end) and both sides see the result.
 */
const POLL_MS = 4000;

function fmt(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export default function BattleOverlay({ me }) {
  const [invite, setInvite] = useState(null);
  const [battle, setBattle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [result, setResult] = useState(null);
  const [now, setNow] = useState(Date.now());
  const dismissed = useRef(new Set());
  const ending = useRef(false);
  const lastActive = useRef(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [p, c] = await Promise.all([
          api.get("/battles/invites/pending"),
          api.get("/battles/mine/current"),
        ]);
        if (!alive) return;
        const next = (p.data || []).find(b => !dismissed.current.has(b.id)) || null;
        setInvite(next);
        const cur = c.data || null;
        // Opponent side: battle disappeared after being active -> fetch final result once.
        if (!cur && lastActive.current) {
          const id = lastActive.current;
          lastActive.current = null;
          api.get(`/battles/${id}`).then(r => { if (alive && r.data?.status === "ended") setResult(r.data); }).catch(() => {});
        }
        if (cur?.status === "active") lastActive.current = cur.id;
        setBattle(cur && (cur.status === "waiting" || cur.status === "active") ? cur : null);
      } catch (e) {
        console.warn("[hifireels] battle poll failed", e?.message || e);
      }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    // Real-time: the backend pushes "battle_invite" over the DM socket — poll immediately
    // instead of waiting for the next tick (which mobile background tabs suspend).
    let ws = null;
    const token = localStorage.getItem("hf_token");
    if (token && API_BASE) {
      try {
        ws = new WebSocket(API_BASE.replace(/^http/, "ws") + `/ws/dm?token=${token}`);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m.type === "battle_invite" || m.type === "battle_accepted" || m.type === "battle_cancelled") tick();
          } catch { /* ignore malformed frames */ }
        };
      } catch { /* ws is best-effort; polling remains the backstop */ }
    }
    return () => { alive = false; clearInterval(t); clearInterval(clock); try { ws && ws.close(); } catch {} };
  }, []);

  // Host closes the battle when the timer runs out.
  useEffect(() => {
    if (!battle || battle.status !== "active" || !battle.ends_at || ending.current) return;
    if (battle.host_user_id !== me?.id) return;
    if (new Date(battle.ends_at).getTime() > now) return;
    ending.current = true;
    api.post(`/battles/${battle.id}/end`)
      .then(r => { setResult({ ...battle, ...r.data }); setBattle(null); lastActive.current = null; })
      .catch(e => setMsg(e?.response?.data?.detail || "Could not close the battle"))
      .finally(() => { ending.current = false; });
  }, [battle, now, me?.id]);

  const accept = async () => {
    if (!invite) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/battles/${invite.id}/accept`);
      dismissed.current.add(invite.id);
      setInvite(null);
      setBattle(data);
      lastActive.current = data.id;
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not accept the battle");
    } finally { setBusy(false); }
  };

  const cancelBattle = async () => {
    if (!battle) return;
    const active = battle.status === "active";
    if (active && !window.confirm("Cancel this PK battle?")) return;
    const id = battle.id;
    try {
      await api.post(`/battles/${id}/cancel`);
      setBattle(null); lastActive.current = null;
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not cancel"); }
  };

  const decline = async () => {
    if (!invite) return;
    dismissed.current.add(invite.id);
    const id = invite.id;
    setInvite(null);
    try { await api.post(`/battles/${id}/decline`); } catch (e) { console.warn("[hifireels] decline failed", e?.message || e); }
  };

  const opponentOf = (b) => (b.host_user_id === me?.id ? b.opponent : b.host) || {};
  const invitedName = (b) => (b.opponent?.username ? `@${b.opponent.username}` : "your opponent");
  const winnerIsMe = result && result.winner_user_id === me?.id;

  return (
    <>
      {battle && battle.status === "waiting" && (
        <div className="absolute top-[5.25rem] left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-full bg-black/75 border border-[#FF7A00]/60 flex items-center gap-2 text-sm font-bold whitespace-nowrap" data-testid="battle-banner">
          <Swords className="w-4 h-4 text-[#FF7A00]"/>
          <span>Waiting for {invitedName(battle)}…</span>
          {battle.host_user_id === me?.id && (
            <button onClick={cancelBattle} data-testid="battle-cancel-invite" className="ml-1 px-2.5 py-0.5 rounded-full bg-[#FF2A5F] text-white text-xs font-extrabold active:scale-95">Cancel</button>
          )}
        </div>
      )}
      {battle && battle.status === "active" && (
        <button onClick={cancelBattle} data-testid="battle-cancel" className="absolute top-[7.7rem] left-1/2 ml-[3.4rem] z-[60] px-2.5 py-1 rounded-full bg-[#FF2A5F]/90 text-white text-[11px] font-extrabold flex items-center gap-1 active:scale-95">
          <X className="w-3 h-3"/>Cancel battle
        </button>
      )}
      {/* NOTE: active-battle "PK Battle vs @xxx" banner removed per support fix #3.
          The timer is now shown exclusively inside <BattleSplitView> score bar. */}

      {msg && (
        <div className="absolute top-28 left-1/2 -translate-x-1/2 z-[60] px-3 py-1.5 rounded-full bg-[#FF2A5F]/90 text-white text-xs font-bold flex items-center gap-2" data-testid="battle-msg">
          {msg}<button onClick={() => setMsg("")}><X className="w-3.5 h-3.5"/></button>
        </div>
      )}

      {result && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/60" data-testid="battle-result">
          <div className="w-72 rounded-3xl bg-[#0B0F19] border border-white/15 p-5 text-center space-y-2">
            <Swords className="w-8 h-8 text-[#FF7A00] mx-auto"/>
            <h3 className="font-outfit font-extrabold text-lg">{winnerIsMe ? "You won the battle! 🏆" : "Battle over"}</h3>
            <p className="text-sm text-white/70">Score {result.host_score ?? 0} : {result.opponent_score ?? 0}</p>
            <button onClick={() => setResult(null)} className="mt-2 w-full py-2.5 rounded-full bg-[#FF7A00] text-black font-bold">OK</button>
          </div>
        </div>
      )}

      {invite && !battle && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/60" data-testid="battle-invite-popup">
          <div className="w-80 rounded-3xl bg-[#0B0F19] border border-[#FF7A00]/50 p-5 text-center space-y-3">
            {invite.host?.avatar && <img src={invite.host.avatar} alt="" className="w-14 h-14 rounded-full mx-auto border border-white/15"/>}
            <h3 className="font-outfit font-extrabold text-lg">⚔️ Battle invite</h3>
            <p className="text-sm text-white/80">@{invite.host?.username || "a creator"} challenged you to a {invite.duration_minutes}-min PK battle</p>
            {invite.invite_message && <p className="text-xs text-white/60 italic">“{invite.invite_message}”</p>}
            <div className="flex gap-2 pt-1">
              <button onClick={decline} disabled={busy} data-testid="battle-decline" className="flex-1 py-2.5 rounded-full bg-white/10 border border-white/15 font-bold">Decline</button>
              <button onClick={accept} disabled={busy} data-testid="battle-accept" className="flex-1 py-2.5 rounded-full bg-[#FF7A00] text-black font-bold flex items-center justify-center gap-1.5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Swords className="w-4 h-4"/>}Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
